import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { useRoute } from "vue-router";
import { mapState } from "pinia";

import { useApp } from "../../App/useApp";
import { IPageCallbackResult } from "../../../resources-vue/vue-interfaces/i-page-container";
import { CentralIntakeService } from "../../../services/central-intake.service";
import { IGetInquiryResult } from "../../../resources-vue/vue-interfaces/i-get-inquiry";
import type { ICreateResponse } from "../../../resources-vue/vue-interfaces/i-response";
import { Inquiry } from "../../../resources-vue/vue-models/inquiry";
import config from "../../../common/config";
import { EnumMap } from "../../../common/enum-map";
import { EnumsService } from "../../../services/enums.service";
import { BranchesService } from "../../../services/branches-service";
import { ToastrService } from "../../../services/toastr.service";
import { IBranchResponse } from "../../../resources-vue/vue-interfaces/i-branch";
import { IMultiSelectDropDownOption } from "../../../resources-vue/vue-elements/MultiSelect/MultiSelect";
import { PermissionManager } from "../../../common/utilities/permission-manager";
import { ApplicationEnum, ApplicationNameEnum, ParentPermissionEnum, PermissionActionEnum } from "../../../enums/enums";
import { IGetAgency, IGetOrganization } from "../../../services/agency.service";
import { DownloadService } from "../../../services/download.service";
import { AnalyticsCategory, AnalyticsService } from "../../../services/analytics.service";

import CentralInquiryRow from "./CentralInquiryRow.vue";
import ListLayout from "../../../resources-vue/vue-custom-elements/ListLayout/ListLayout.vue";
import SortableTh from "../../../resources-vue/vue-custom-elements/SortableTh/SortableTh.vue";
import ErrorMessageHandler from "../../../resources-vue/vue-custom-elements/ErrorMessageHandler/ErrorMessageHandler.vue";
import BranchDefaultFilter from "../../../resources-vue/vue-custom-elements/BranchDefaultFilter/BranchDefaultFilter.vue";
import InquiryForm from "../../../resources-vue/vue-forms/InquiryForm/InquiryForm.vue";
import InquiryFormHomecareHomehealth from "../../../resources-vue/vue-forms/InquiryFormHomecareHomehealth/InquiryFormHomecareHomehealth.vue";
import Pagination from "../../../resources-vue/vue-elements/Pagination/Pagination.vue";
import MultiSelect from "../../../resources-vue/vue-elements/MultiSelect/MultiSelect.vue";
import StaticTableFormater from "../../../resources-vue/vue-custom-elements/StaticTableFormater/StaticTableFormater.vue";

export const PAGE_FAIL_RESPONSE: IPageCallbackResult = {
    totalPageCount: 0,
    success: false,
    totalCount: 0
};

export default defineComponent({
    components: {
        Pagination,
        CentralInquiryRow,
        ErrorMessageHandler,
        SortableTh,
        ListLayout,
        BranchDefaultFilter,
        InquiryForm,
        InquiryFormHomecareHomehealth,
        MultiSelect,
        StaticTableFormater
    },
    data() {
        return {
            _analyticsService: null as AnalyticsService,
            _bindingEngine: null,
            _centralIntakeService: null,
            _toastrService: null,
            _enumsService: null,
            _branchService: null,
            _permissionManager: null,
            _downloadService: null,
            count: 0,
            pageNumber: 1,
            pageSize: 10,
            isLoading: false,
            selectedBranches: [] as string[],
            responseHandler: { data: null, stamp: null, requests: [] },
            isError: false,
            sortBy: "date",
            sortOrder: "desc",
            term: "",
            page: null,
            branchRef: null,
            columns: 9,
            pageOrder: 10,
            hideTitle: false,
            isAddItemRequested: false,
            lineOfServiceEnum: [],
            lineOfServiceEnumMap: new EnumMap([]),
            referralsAddable: [],
            branches: [],
            paginationInstance: null,
            canAddHospiceInquiry: false,
            canAddPalliativeInquiry: false,
            canAddHomeCareInquiry: false,
            canAddHomeHealthInquiry: false,
            moveListDown: false,
            isSaveNewInProgress: false,
            isEditSaveInProgress: false,
            excelExportPermission: false,
            newInquiry: null,
            serviceTypeEnums: [],
            selectedBranchesLength: 0,
            currentAddBranches: [],
            hospiceBranches: [],
            palliativeBranches: [],
            homeCareBranches: [],
            homeHealthBranches: [],
            currentEditBranches: [],
            searchTimer: null,
            newInquiryId: "",
            showNewInquiryDropdown: true,
            agencyMultiSelectOptions: {} as IMultiSelectDropDownOption,
            selectedAgencies: [] as string[],
            selectedAgenciesName: [] as string[],
            selectOrganization: [] as IGetOrganization[],
            agencyList: [] as IGetAgency[],
            route: useRoute(),
            dynamicTableToggled: false
        };
    },
    async created() {
        this._centralIntakeService = Container.instance.get(CentralIntakeService);
        this._enumsService = Container.instance.get(EnumsService);
        this._branchService = Container.instance.get(BranchesService);
        this._toastrService = Container.instance.get(ToastrService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._downloadService = Container.instance.get(DownloadService);
        this._analyticsService = Container.instance.get(AnalyticsService);
        this.lineOfServiceEnum = await this._enumsService.getLineOfService();
        this.lineOfServiceEnumMap = new EnumMap(this.lineOfServiceEnum);
        this.serviceTypeEnums = await this._enumsService.getLineOfService();
        this.branches = await this._branchService.getAllBranches();
        await this.fillReferralsAddable();
        this.paginationInstance = this.$refs?.paginationInstance as typeof Pagination;
        this.hospiceBranches = this.branches.filter((branch) => branch.application == ApplicationEnum.AxxessHospiceFE);
        this.palliativeBranches = this.branches.filter(
            (branch) => branch.application == ApplicationEnum.AxxessPalliative
        );
        this.homeCareBranches = this.branches.filter((branch) => branch.application == ApplicationEnum.HomeCare);
        this.homeHealthBranches = this.branches.filter((branch) => branch.application == ApplicationEnum.AgencyCore);
        this.isLoading = true;
    },

    async mounted() {
        if (this.$route.query.term) {
            this.term = this.$route.query.term as string;
        }
        const organizationId = localStorage.getItem(config.organizationId);
        this.selectOrganization = this.organizations.filter((organization) => organization.id == organizationId);

        const selectedAgencies = this.selectOrganization[0].agencies;
        this.agencyList = selectedAgencies.map((agency) => {
            let agencyAndApplicationIds = agency.id.split("_");
            let application = parseInt(agencyAndApplicationIds[1], 10);

            return {
                ...agency,
                agencyId: agencyAndApplicationIds[0],
                application: application
            };
        });

        const canAddHosInquiry = this.agencyList.some((el: any) => el.application === ApplicationEnum.AxxessHospiceFE);
        const canAddPalInquiry = this.agencyList.some((el: any) => el.application === ApplicationEnum.AxxessPalliative);
        const canAddHcInquiry = this.agencyList.some((el: any) => el.application === ApplicationEnum.HomeCare);
        const canAddHhInquiry = this.agencyList.some((el: any) => el.application === ApplicationEnum.AgencyCore);

        this.paginationInstance = this.$refs?.paginationInstance as typeof Pagination;
        this.canAddHospiceInquiry =
            this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.inquiries,
                PermissionActionEnum.canAdd,
                ApplicationEnum.AxxessHospiceFE
            ) && canAddHosInquiry;
        this.canAddPalliativeInquiry =
            this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.inquiries,
                PermissionActionEnum.canAdd,
                ApplicationEnum.AxxessPalliative
            ) && canAddPalInquiry;
        this.canAddHomeCareInquiry =
            this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.inquiries,
                PermissionActionEnum.canAdd,
                ApplicationEnum.HomeCare
            ) && canAddHcInquiry;
        this.canAddHomeHealthInquiry =
            this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.inquiries,
                PermissionActionEnum.canAdd,
                ApplicationEnum.AgencyCore
            ) && canAddHhInquiry;
        await this.setAgencyMultiSelect();
        if (this.$route.query.agencies) {
            const agencies = (this.$route.query.agencies as string).split(",");
            this.updateBranchesbyAgency(agencies);
        }
    },

    computed: {
        ...mapState(useApp, ["organizations"]),
        hasData() {
            return this.page?.itemCount > 0;
        },

        loadingCount() {
            return this.page?.items?.length > 0 ? this.page.items.length : config.pageSize;
        },

        hospiceApplicationId() {
            return ApplicationEnum.AxxessHospiceFE;
        },

        palliativeApplicationId() {
            return ApplicationEnum.AxxessPalliative;
        },

        homeCareApplicationId() {
            return ApplicationEnum.HomeCare;
        },

        homeHealthApplicationId() {
            return ApplicationEnum.AgencyCore;
        },

        isApplicationHomeCare(): boolean {
            return this.newInquiry.application == ApplicationEnum.HomeCare;
        },

        isApplicationHomeHealth(): boolean {
            return this.newInquiry.application == ApplicationEnum.AgencyCore;
        }
    },

    watch: {
        term() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(
                (term) => {
                    if (term?.length > 2 || term === "") {
                        this.search();
                        this.$router.replace({ query: { ...this.route.query, term: this.term } });
                    }
                },
                500,
                this.term
            );
        },
        selectedBranches: {
            handler() {
                this.pageReset();
            },
            deep: true
        }
    },

    methods: {
        sortByUpdated(value: string) {
            if (value != this.sortBy) {
                this.sortBy = value;
                this.reset();
            }
        },

        sortOrderUpdated(value: string) {
            if (value != this.sortOrder) {
                this.sortOrder = value;
                this.reset();
            }
        },

        updatePageSizeChanged(value: number) {
            this.pageSize = value;
        },

        reset() {
            this.paginationInstance?.reset();
        },

        async fillReferralsAddable() {
            this.referralsAddable = this.branches.filter((branch: IBranchResponse) =>
                this.isReferralAddAllowed(branch)
            );
        },

        isReferralAddAllowed(branch: IBranchResponse): boolean {
            if (!branch.application || !branch.accountId) return false;
            return this._permissionManager.checkPermission(
                ParentPermissionEnum.referrals,
                PermissionActionEnum.canAdd,
                branch.accountId
            );
        },

        async pageChangedCallback(pageNumber: number, pageSize: number): Promise<IPageCallbackResult> {
            this.pageNumber = pageNumber;
            this.pageSize = pageSize;
            if (this.selectedBranches.length === 0) {
                this.page = null;
                return PAGE_FAIL_RESPONSE;
            }
            try {
                let callTime = Date.now();
                this.responseHandler.stamp = callTime;
                this.isLoading = true;
                this.responseHandler.data = null;
                this.isError = false;
                let payLoad = {
                    page: pageNumber,
                    pageLength: pageSize.toString(),
                    sortBy: this.sortBy,
                    order: this.sortOrder,
                    term: this.term,
                    locationIds: this.selectedBranches,
                    agencyIds: this.selectedAgencies.map((agency) => agency.split("_")[0])
                };
                let response = this._centralIntakeService.inquiries(payLoad);
                this.responseHandler.requests.push(response);
                let data: IGetInquiryResult = await response;
                if (this.responseHandler.stamp == callTime) {
                    this.responseHandler.data = data;
                } else {
                    data =
                        this.responseHandler.data ||
                        (await this.responseHandler.requests[this.responseHandler.requests.length - 1]);
                }
                this.page = data;
                this.page.items = this.page.items.map((item: Inquiry) => new Inquiry(item.application, item, true));
                return {
                    totalPageCount: data.pageCount,
                    success: data.itemCount > 0,
                    totalCount: data.itemCount
                };
            } catch (e) {
                this.isError = true;
                return PAGE_FAIL_RESPONSE;
            } finally {
                this.isLoading = false;
            }
        },

        search() {
            if (!this.isLoading) {
                this.pageReset();
            }
        },

        pageReset() {
            this.isError = false;
            this.reset();
        },

        updateBranches(branches: string[]) {
            this.selectedBranches.length = 0;
            this.selectedBranches.push(...branches);
            let branchesData = this.branches.filter((branch) => this.selectedBranches?.includes(branch.id));
            const selectedAgenciesId = [] as any[];
            this.agencyList.filter((agency) => {
                branchesData.some((branch) => {
                    const { application, accountId, providerId } = branch;
                    if (
                        (application == ApplicationEnum.HomeCare || application == ApplicationEnum.AgencyCore) &&
                        agency.id.includes(accountId)
                    ) {
                        selectedAgenciesId.push(agency.id);
                        return true;
                    }
                    if (
                        (application == ApplicationEnum.AxxessHospiceFE ||
                            application == ApplicationEnum.AxxessPalliative) &&
                        agency.id.includes(accountId) &&
                        agency.id.includes(providerId)
                    ) {
                        selectedAgenciesId.push(agency.id);
                        return true;
                    }
                    return false;
                });
            });
            this.canShowexcelExport(selectedAgenciesId);
        },

        async setAgencyMultiSelect() {
            try {
                this.agencyMultiSelectOptions = {};
                this.selectedAgencies.length = 0;

                if (!this.agencyList || this.agencyList?.length === 0) {
                    return;
                }
                let allowedProducts = [
                    ApplicationEnum.AgencyCore,
                    ApplicationEnum.HomeCare,
                    ApplicationEnum.AxxessHospice,
                    ApplicationEnum.AxxessHospiceFE,
                    ApplicationEnum.AxxessPalliative
                ];
                const schedulerViewPermissions = this._permissionManager.getPermmission(
                    ParentPermissionEnum.inquiries,
                    PermissionActionEnum.canView
                );
                let schedulerAgencies = schedulerViewPermissions
                    .filter((item: any) => {
                        if (!item) {
                            return false;
                        }
                        let application = item.application;
                        if (application === ApplicationEnum.AxxessHospiceFE) {
                            application = ApplicationEnum.AxxessHospice;
                        }
                        return allowedProducts.includes(application);
                    })
                    .map((x: any) => x.agencyId);
                let filterableProducts = this.agencyList.filter((agency) => {
                    let isAllowed = allowedProducts.includes(agency.application);
                    return isAllowed && schedulerAgencies.includes(agency.agencyId);
                });
                filterableProducts?.forEach((agency) => {
                    let appName =
                        ApplicationNameEnum[
                            agency.application === ApplicationEnum.AxxessHospiceFE
                                ? ApplicationEnum.AxxessHospice
                                : agency.application
                        ];

                    if (!this.agencyMultiSelectOptions[appName]) {
                        this.agencyMultiSelectOptions[appName] = [];
                    }

                    this.agencyMultiSelectOptions[appName].push({
                        displayText: agency.name,
                        value: agency.id
                    });
                });

                Object.values(this.agencyMultiSelectOptions).forEach((agency: any) => {
                    let values = agency.map((item: any) => item.value);
                    this.selectedAgencies.push(...values);
                });
                this.canShowexcelExport(this.selectedAgencies);
                // For select agencies change
            } catch (e) {
                console.warn(e);
                // Silent fail
            }
        },

        updateBranchesbyAgency(agencies: string[]) {
            this.selectedAgencies.length = 0;
            this.selectedAgencies.push(...agencies);
            if (this.selectedAgencies.length !== this.agencyList.length) {
                const queryAgencies = this.selectedAgencies.filter((_, index: number) => index < 12);
                this.$router.replace({
                    query: { term: this.term ? this.term : "", agencies: queryAgencies.join(",") }
                });
            } else {
                this.$router.replace({
                    query: { term: this.term ? this.term : "", agencies: "" }
                });
            }
            this.canShowexcelExport(this.selectedAgencies);
            this.selectedAgenciesName = this.selectOrganization[0].agencies
                .filter((agency) => this.selectedAgencies.includes(agency.id))
                .map((agency) => agency.name);
        },

        canShowexcelExport(selectedAgencies: string[]) {
            this.excelExportPermission = selectedAgencies.some((agency) => {
                let agencyData = this.agencyList.find((agn) => agn.id === agency);
                const action =
                    agencyData.application == ApplicationEnum.HomeCare ||
                    agencyData.application == ApplicationEnum.AgencyCore
                        ? PermissionActionEnum.canExport
                        : agencyData.application == ApplicationEnum.AxxessPalliative ||
                            agencyData.application == ApplicationEnum.AxxessHospiceFE
                          ? PermissionActionEnum.canView
                          : "";
                return (
                    action &&
                    this._permissionManager.checkPermission(ParentPermissionEnum.inquiries, action, agencyData.agencyId)
                );
            });
        },

        initInquiry(application: ApplicationEnum) {
            return new Inquiry(application, null, true);
        },
        async downloadChart() {
            try {
                const payLoad = {
                    page: this.pageNumber,
                    pageLength: this.pageSize.toString(),
                    sortBy: this.sortBy,
                    order: this.sortOrder,
                    term: this.term,
                    locationIds: this.selectedBranches,
                    agencyIds: this.selectedAgencies.map((agency) => agency.split("_")[0]),
                    Export: true
                };
                const response = await this._centralIntakeService.exportInquiries(payLoad);
                const warningMessage = response.headers.get("x-warning-message");
                if (warningMessage) {
                    this._toastrService.warning({
                        title: `Export Excel Warning`,
                        message: warningMessage
                    });
                }
                this._downloadService.downloadFile(response, response.type);
                this._toastrService.success({
                    title: `Success`,
                    message: `Inquiry Export Excel downloaded.`
                });
            } catch (error) {
                this._toastrService.error({
                    title: `Error`,
                    message: "There was a problem while Export Excel of the referrals. Please try again."
                });
            }
        },

        openAddInquiry(applicationId: ApplicationEnum) {
            this.isAddItemRequested = true;
            this.moveListDown = true;
            this.newInquiry = this.initInquiry(applicationId);
            this.newInquiry.application = applicationId;
            this.showNewInquiryDropdown = false;
        },

        closeAddInquiry(application: ApplicationEnum) {
            this.newInquiry = this.initInquiry(application);
            this.isAddItemRequested = false;
            this.moveListDown = false;
        },

        updateAddBranch(currentApplication: ApplicationEnum) {
            switch (currentApplication) {
                case ApplicationEnum.AxxessHospiceFE:
                    this.currentAddBranches = this.hospiceBranches;
                    break;
                case ApplicationEnum.AxxessPalliative:
                    this.currentAddBranches = this.palliativeBranches;
                    break;
                case ApplicationEnum.HomeCare:
                    this.currentAddBranches = this.homeCareBranches;
                    break;
                case ApplicationEnum.AgencyCore:
                    this.currentAddBranches = this.homeHealthBranches;
                    break;
                default:
                    this.currentAddBranches = [];
            }
        },

        updateEditBranch(applicationId: ApplicationEnum) {
            switch (applicationId) {
                case ApplicationEnum.AxxessHospiceFE:
                    this.currentEditBranches = this.hospiceBranches;
                    break;
                case ApplicationEnum.AxxessPalliative:
                    this.currentEditBranches = this.palliativeBranches;
                    break;
                case ApplicationEnum.HomeCare:
                    this.currentEditBranches = this.homeCareBranches;
                    break;
                case ApplicationEnum.AgencyCore:
                    this.currentEditBranches = this.homeHealthBranches;
                    break;
                default:
                    this.currentEditBranches = [];
            }
        },
        async saveNewInquiry() {
            try {
                let validated: boolean;
                if (this.isApplicationHomeCare || this.isApplicationHomeHealth) {
                    validated = await (this.$refs.homecareInquiryForm as any).v$.$validate();
                } else {
                    validated = await (this.$refs.inquiryForm as any).v$.$validate();
                }
                if (validated) {
                    this.isSaveNewInProgress = true;
                    let result: ICreateResponse = await this._centralIntakeService.createInquiry(this.newInquiry);
                    this.isSaveNewInProgress = false;
                    this.closeAddInquiry(this.newInquiry.application);
                    this.newInquiryId = result.id;
                    this.newInquiry = this.initInquiry(this.newInquiry.application);
                    this._toastrService.success({
                        title: `Inquiry Added`,
                        message: `Inquiry has been successfully added.`
                    });
                    this._analyticsService.logEvent({
                        category: AnalyticsCategory.CentralIntake,
                        action: "Central New Inquiry"
                    });
                    await this.paginationInstance.reset();
                    this.emphasizeNewItem();
                } else {
                    this._toastrService.error({
                        title: `Validation Failed`,
                        message: `Fill all required fields.`
                    });
                }
            } catch (e) {
                this.isSaveNewInProgress = false;
            }
        },
        emphasizeNewItem() {
            let newTimer: number = 0;
            let newInquiryAnimationLimit = 3; // 5 seconds
            let intervalId = setInterval(() => {
                newTimer++;
                if (newTimer > newInquiryAnimationLimit) {
                    this.newInquiryId = "";
                    clearInterval(intervalId);
                }
            }, 1000);
        },
        getCustomSettings() {
            return [
                { Width: 120, RowIndex: 1 },
                { Width: 128, RowIndex: 2 },
                { Width: 100, RowIndex: 8 }
            ];
        }
    }
});
